<script lang="ts">
    import type { IconType } from "@components/material/Icon.module"
    import Icon from "@components/material/Icon.svelte"

    export let href: string
    export let icon: IconType
    export let label: string
    export let active = false
    export let collapsed = false
    export let badge = false
</script>

<a
    class="primary-nav-item"
    class:active
    class:collapsed
    aria-current={active ? "page" : undefined}
    data-tooltip={label}
    {href}
    on:click
>
    <span class="icon-wrap">
        <Icon {icon} />
        {#if badge}
            <span class="badge-dot" aria-hidden="true" />
        {/if}
    </span>
    <span class="nav-label">{label}</span>
</a>

<style>
    .primary-nav-item {
        display: grid;
        grid-template-columns: 1.75rem minmax(0, 1fr);
        gap: 0.75rem;
        align-items: center;
        position: relative;
        min-height: 2.75rem;
        padding: 0.625rem 0.75rem;
        border-radius: 0.5rem;
        color: var(--primary-nav-text, var(--color-grey-600));
        text-decoration: none;
        transition:
            background-color 0.16s ease,
            color 0.16s ease,
            transform 0.16s ease;
    }

    .primary-nav-item:hover {
        color: var(--primary-nav-accent, var(--color-purple-700));
        background: var(--primary-nav-accent-soft, var(--color-purple-50));
    }

    .primary-nav-item.active {
        color: var(--primary-nav-active-text, var(--color-grey-0));
        background: var(--primary-nav-active-bg, var(--color-purple-700));
    }

    .primary-nav-item:active {
        transform: translateY(1px);
    }

    .icon-wrap {
        width: 1.75rem;
        height: 1.75rem;
        display: grid;
        place-items: center;
        position: relative;
    }

    .icon-wrap :global(svg) {
        width: 1.25rem;
        height: 1.25rem;
    }

    .nav-label {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: 0.92rem;
        font-weight: 700;
        letter-spacing: 0;
    }

    .badge-dot {
        position: absolute;
        top: 0.42rem;
        right: 0.42rem;
        width: 0.58rem;
        height: 0.58rem;
        border-radius: 999px;
        background: var(--color-gold-400);
        box-shadow: 0 0 0 0.18rem var(--primary-nav-badge-ring, var(--color-grey-0));
    }

    .primary-nav-item.collapsed {
        grid-template-columns: 1fr;
        justify-items: center;
    }

    .primary-nav-item.collapsed::after {
        content: attr(data-tooltip);
        position: absolute;
        top: 50%;
        left: calc(100% + 0.75rem);
        min-width: max-content;
        max-width: 12rem;
        padding: 0.42rem 0.7rem;
        border-radius: 999px;
        box-shadow: var(--primary-nav-tooltip-shadow, 0 0.55rem 1.45rem rgb(41 34 74 / 0.18));
        background: var(--primary-nav-tooltip-bg, var(--color-grey-900));
        color: var(--primary-nav-tooltip-text, var(--color-grey-0));
        font-size: 0.78rem;
        font-weight: 800;
        line-height: 1;
        opacity: 0;
        pointer-events: none;
        transform: translate(0.35rem, -50%);
        transition:
            opacity 0.14s ease,
            transform 0.14s ease;
        white-space: nowrap;
        z-index: 30;
    }

    .primary-nav-item.collapsed:hover::after,
    .primary-nav-item.collapsed:focus-visible::after {
        opacity: 1;
        transform: translate(0, -50%);
    }

    .primary-nav-item.collapsed .nav-label {
        position: absolute;
        width: 1px;
        height: 1px;
        overflow: hidden;
        clip: rect(0 0 0 0);
        white-space: nowrap;
    }

    @media only screen and (max-width: 1153px) {
        .primary-nav-item,
        .primary-nav-item.collapsed {
            grid-template-columns: 1fr;
            grid-template-rows: 1.8rem 1rem;
            align-content: center;
            align-items: center;
            justify-items: center;
            gap: 0.16rem;
            min-height: 3.75rem;
            padding: 0;
            border-radius: 0.5rem;
            color: var(--primary-nav-muted, var(--color-grey-500));
        }

        .primary-nav-item:hover {
            color: var(--primary-nav-accent, var(--color-purple-700));
            background: var(--primary-nav-accent-soft, var(--color-purple-50));
        }

        .primary-nav-item.active {
            color: var(--primary-nav-accent, var(--color-purple-700));
            background: transparent;
        }

        .icon-wrap {
            width: 1.8rem;
            height: 1.8rem;
        }

        .icon-wrap :global(svg) {
            width: 1.18rem;
            height: 1.18rem;
        }

        .nav-label,
        .primary-nav-item.collapsed .nav-label {
            position: static;
            width: auto;
            height: auto;
            clip: auto;
            display: flex;
            align-items: center;
            justify-content: center;
            max-width: 100%;
            font-size: 0.68rem;
            line-height: 1;
            font-weight: 700;
        }

        .primary-nav-item.collapsed::after {
            content: none;
        }

        .badge-dot {
            top: 0.18rem;
            right: 0.18rem;
        }
    }

    @media only screen and (max-width: 360px) {
        .nav-label,
        .primary-nav-item.collapsed .nav-label {
            font-size: 0.62rem;
        }

        .primary-nav-item,
        .primary-nav-item.collapsed {
            gap: 0.08rem;
            min-height: 3.45rem;
        }
    }
</style>
